import type { TOC } from '@ember/component/template-only';
import Checkbox from './checkbox';

// ---------------------------------------------------------------------------
// CheckboxGroup – wrapper for grouping multiple Checkbox components
// ---------------------------------------------------------------------------

interface CheckboxGroupSignature {
  Args: {
    /** Group label */
    label?: string;
    /** Hint text displayed below the label */
    hint?: string;
    /** Validation error messages for the group */
    errors?: string[];
    /** Show required indicator on label */
    isRequired?: boolean;
    /** Disable all child checkboxes (applies HTML disabled to the fieldset) */
    isDisabled?: boolean;
    /** Layout direction @default "vertical" */
    orientation?: 'vertical' | 'horizontal';
  };
  Blocks: {
    /** Yields Checkbox component for block mode, or accepts direct children */
    default: [typeof Checkbox];
  };
  Element: HTMLFieldSetElement;
}

/**
 * Reimagined CheckboxGroup — groups multiple Checkbox components with a shared
 * label, hint, and error display.
 *
 * @example Vertical group
 * ```gts
 * import CheckboxGroup from 'tio-ui/components/checkbox-group';
 * import Checkbox from './checkbox';
 *
 * <CheckboxGroup @label="Notifications" @isRequired={{true}}>
 *   <Checkbox @label="Email" @checked={{this.emailEnabled}} @onChange={{this.setEmail}} />
 *   <Checkbox @label="SMS" @checked={{this.smsEnabled}} @onChange={{this.setSms}} />
 *   <Checkbox @label="Push" @checked={{this.pushEnabled}} @onChange={{this.setPush}} />
 * </CheckboxGroup>
 * ```
 *
 * @example Horizontal group with errors
 * ```gts
 * <CheckboxGroup
 *   @label="Select at least one"
 *   @orientation="horizontal"
 *   @errors={{array "Please select at least one option"}}
 * >
 *   <Checkbox @label="Option A" @checked={{this.a}} @onChange={{this.setA}} />
 *   <Checkbox @label="Option B" @checked={{this.b}} @onChange={{this.setB}} />
 * </CheckboxGroup>
 * ```
 */
const CheckboxGroup: TOC<CheckboxGroupSignature> = <template>
  <fieldset
    data-test-checkbox-group
    class="flex flex-col gap-2 {{if @isDisabled 'opacity-50'}}"
    disabled={{@isDisabled}}
    ...attributes
  >
    {{#if @label}}
      <legend data-test-checkbox-group-label class="tio-text-sm-medium tio-fg-secondary mb-1">
        {{@label}}
        {{#if @isRequired}}
          <span class="tio-fg-danger ml-0.5" aria-hidden="true">*</span>
        {{/if}}
      </legend>
    {{/if}}

    {{#if @hint}}
      <p data-test-checkbox-group-hint class="text-xs tio-fg-muted -mt-1 mb-1">{{@hint}}</p>
    {{/if}}

    <div
      class="flex
        {{if (isHorizontal @orientation) 'flex-row flex-wrap gap-x-6 gap-y-2' 'flex-col gap-2'}}"
    >
      {{yield Checkbox}}
    </div>

    {{#if (hasErrors @errors)}}
      <div data-test-checkbox-group-errors class="space-y-0.5">
        {{#each @errors as |error|}}
          <p class="text-xs tio-fg-danger" role="alert">{{error}}</p>
        {{/each}}
      </div>
    {{/if}}
  </fieldset>
</template>;

// Helper functions for template use
function isHorizontal(orientation?: string): boolean {
  return orientation === 'horizontal';
}

function hasErrors(errors?: string[]): boolean {
  return Boolean(errors?.length);
}

export default CheckboxGroup;
export type { CheckboxGroupSignature };
